/** Microsoft Teams bot operational issues. */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY, ISSUE_FAULT } from "../issue-enums";

export const TEAMS_ISSUES = {
  TEAMS_BOT_TOKEN_EXPIRED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Microsoft Teams bot token is invalid or expired",
    // High, not critical, for the credential mismatch's reasons: the 401 is the customer's own bot
    // credential (`USER_ACTION`, repaired by reconnecting Teams), no operator lever repairs it, it
    // pages nobody, and its blast radius is one deployment.
    severity: "high",
    // Filed per DEPLOYMENT on every failed outbound send of the bot, and nothing re-checks the
    // bot between sends, so silence is the only evidence it recovered. Same week as the
    // credential mismatch: a bot still broken and still in use reports again within days.
    resolvesAfterQuietHours: 168,
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Teams connection expired",
    userGuidance:
      "Your Microsoft Teams bot token has expired, so messages can't be sent or received. Reconnect Teams to restore the integration."
  },
  TEAMS_BOT_CREDENTIAL_MISMATCH: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Teams bot credentials do not match the target tenant",
    // High, not critical: the customer acts (`USER_ACTION`, and the banner below tells them how),
    // no operator lever repairs it, it pages nobody, and its blast radius is one deployment.
    severity: "high",
    // Raised on every failed outbound send of the deployment's bot, so a bot still broken and
    // still in use reports again within days. A week of silence means it was reconnected or
    // nobody is sending through it.
    resolvesAfterQuietHours: 168,
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Teams credentials don't match tenant",
    userGuidance:
      "The bot's credentials don't match the target Microsoft tenant. Reconnect Teams from the correct tenant to fix it."
  },
  TEAMS_CONVERSATION_NOT_FOUND: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Teams conversation or user ID is no longer valid",
    // Medium: one conversation Microsoft no longer knows. The bot and every other conversation
    // still work, no operator or customer lever revives the thread, and it pages nobody — the
    // rank of its siblings in `one-dead-conversation.registry.test.ts`.
    severity: "medium",
    // Reported per CONVERSATION on a failed send, and nothing retries a conversation Microsoft no
    // longer knows. A week without another failed send to it means nobody is trying to reach it
    // any more — the same window as Telegram's dead chat.
    resolvesAfterQuietHours: 168,
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "Teams conversation no longer valid",
    userGuidance:
      "A Teams conversation or user reference is no longer valid on Microsoft's side, so that thread can't be reached."
  },
  TEAMS_GRAPH_API_THROTTLE: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Microsoft Graph API rate limit exceeded",
    severity: "medium",
    // A throttle clears on its own within minutes; a day without another throttled send to the
    // bot means it cleared.
    resolvesAfterQuietHours: 24,
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "Microsoft Graph rate limit reached",
    userGuidance:
      "Microsoft Graph is throttling requests. Delivery catches up on its own once the limit clears."
  },
  TEAMS_INSUFFICIENT_PERMISSION: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Teams bot is missing required Microsoft Graph permissions",
    severity: "high",
    // Filed per DEPLOYMENT on every failed outbound send of the bot, and nothing re-checks the
    // bot between sends, so silence is the only evidence it recovered. Same week as the
    // credential mismatch: a bot still broken and still in use reports again within days.
    resolvesAfterQuietHours: 168,
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Teams permissions out of date",
    userGuidance:
      "Your Teams bot is missing Microsoft Graph permissions it now needs. Reconnect Teams to grant the updated permissions."
  },
  /**
   * An INBOUND Teams activity was rejected by Bot Framework authentication.
   *
   * The other codes in this file describe OUTBOUND failures — we called
   * Microsoft and Microsoft refused. This one is the opposite direction: a
   * request arrived at our webhook carrying a token whose audience is supposed
   * to be the receiving bot's own Microsoft app id, and it did not validate.
   *
   * ## Deliberately admin-only — no `fault`, no `userTitle`, no `userGuidance`
   *
   * Every other code here is raised by code we invoked. This one is raised by
   * an ANONYMOUS caller: the Teams webhook routes are `@AllowUnauthenticated`
   * by necessity (Microsoft cannot present a Nexus credential), so anyone who
   * knows a customer's webhook URL can make this fire. Giving it customer-facing
   * presentation fields would hand that caller a banner in the customer's own
   * dashboard saying their Teams integration is broken when it is not.
   *
   * Dedupe on (organizationId, code, entityType, entityId) caps that at one row
   * per deployment, so the reachable damage is a stale `count` and a `lastSeenAt`
   * on an operator surface — not a customer-visible alarm and not row growth.
   *
   * The operator audience is the right one anyway: the condition this exists to
   * catch is a bot whose STORED Microsoft app id has drifted from the one
   * Microsoft addresses, which is repaired by re-provisioning the bot, not by
   * anything the end user can do from a chat window.
   */
  TEAMS_WEBHOOK_AUTH_REJECTED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message:
      "Inbound Teams activity rejected by Bot Framework authentication — this bot receives nothing until its stored Microsoft app id matches the inbound token's audience",
    // High, not critical: it pages nobody, and an anonymous caller holding a webhook URL can
    // raise it (see above) — a severity anyone on the internet can mint is not the top rung.
    severity: "high",
    // Raised on every rejected inbound activity, and Microsoft redelivers to a live bot, so a bot
    // still rejecting reports again within days. A week of silence means it recovered or is idle.
    resolvesAfterQuietHours: 168,
    remedy:
      "Open the issue's raw payload and read `audienceMatchesStoredClientId`. " +
      "FALSE means the bot's ApiKeyConnection holds a stale Microsoft app id — `storedClientId` is what Nexus has, " +
      "`unverifiedTokenAudience` is the app Microsoft is addressing. Re-provision or reconnect that Teams bot in Azure so " +
      "the stored credentials name the app in the audience, then confirm the next inbound activity is accepted and mark " +
      "this issue resolved. " +
      "TRUE means the ids agree and the rejection is something else — an expired token, clock skew, or a signing-key " +
      "problem; read the backend logs for `TeamsWebhookVerifier`. " +
      "NULL means no audience could be read: check `tokenReadable`, and if it is false the caller did not send a " +
      "Bot Framework token at all, which is an unauthenticated caller rather than a broken bot. " +
      "`requestAccepted: true` means the activity was let through anyway because `deployments`/`teams-webhook-auth` is " +
      'set to {"mode":"log"}; DELETE that SystemConfig row to return to enforcement — the code default is `enforce`.'
  }
} as const satisfies Record<string, IssueDefinition>;
